import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getOrders,
  getOrderById,
  createOrder,
  updateOrder,
  deleteOrder,
  updateOrderStatus,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { OrderCreation } from "types";
import { toast } from "react-toastify";
// import { OrderType } from "types";

// Fetch all orders
export const useGetOrders = (branch_id?: string | number) => {
  return useQuery({
    queryKey: [queryKeys.orders, branch_id],
    queryFn: async () => await getOrders(branch_id),
  });
};

// Fetch a single order by ID
export const useGetOrderbyId = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.orders, id],
    queryFn: async () => await getOrderById(id),
    enabled: !!id,
  });
};

// Create a new order
export const useCreateOrder = () => {
  return useMutation<any, Error, FormData>({
    mutationFn: createOrder,
    onSuccess: async (data: OrderCreation) => {
      toast.success("Order submitted successfully");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orders],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateOrderPayload = {
  id: string;
  data: OrderCreation;
};

// Update order hook
export const useUpdateOrder = (): UseMutationResult<
  any,
  Error,
  UpdateOrderPayload
> => {
  return useMutation<any, Error, UpdateOrderPayload>({
    mutationFn: ({ id, data }: UpdateOrderPayload) => updateOrder(id, data),
    onSuccess: async () => {
      // Invalidate orders query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orders],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateOrderStatus = {
  id: string;
  data: { status: string };
};

// Update order status
export const useUpdateOrderStatus = (): UseMutationResult<
  any,
  Error,
  UpdateOrderStatus
> => {
  return useMutation<any, Error, UpdateOrderStatus>({
    mutationFn: ({ id, data }: UpdateOrderStatus) =>
      updateOrderStatus(id, data),
    onSuccess: async () => {
      toast.success("Order status updated successful");
      // Invalidate order query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orders],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete an order
export const useDeleteOrder = () => {
  return useMutation({
    mutationFn: deleteOrder,
    onSuccess: async () => {
      toast.success("Order deleted successfully");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.orders],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
